import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { uploadToCloudinary, validateFile } from '../utils/cloudinaryUpload';
import { 
  ArrowLeft, 
  Save, 
  Upload, 
  X, 
  User, 
  Image as ImageIcon,
  Camera,
  Trash2,
  Edit3,
  Key
} from 'lucide-react';
import '../styles/UserProfileEdit.css';

const UserProfileEdit = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    nickname: '',
    handle: '',
    bio: '',
    mobile_phone: '',
    instagram: '',
    user_type: 'collector',
    commissions: false,
    test_account: false,
    profile_img: '',
    cover_image: ''
  });

  const [originalProfile, setOriginalProfile] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Image upload states
  const [uploadingImage, setUploadingImage] = useState(false);

  // Password reset states
  const [resettingPassword, setResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  useEffect(() => {
    if (userId) {
      loadUserProfile();
    }
  }, [userId]);

  useEffect(() => {
    // Check if there are unsaved changes
    if (originalProfile) {
      const changed = JSON.stringify(profile) !== JSON.stringify(originalProfile);
      setHasChanges(changed);
    }
  }, [profile, originalProfile]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError('');

      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('uuid', userId)
        .single();

      if (error) {
        throw error;
      }

      setProfile({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        nickname: userData.nickname || '',
        handle: userData.handle || '',
        bio: userData.bio || '',
        mobile_phone: userData.mobile_phone || '',
        instagram: userData.instagram || '',
        user_type: userData.preferences?.home || 'collector',
        commissions: userData.commissions || false,
        test_account: userData.test_account || false,
        profile_img: userData.profile_img || '',
        cover_image: userData.cover_image || ''
      });

      setOriginalProfile({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        nickname: userData.nickname || '',
        handle: userData.handle || '',
        bio: userData.bio || '',
        mobile_phone: userData.mobile_phone || '',
        instagram: userData.instagram || '',
        user_type: userData.preferences?.home || 'collector',
        commissions: userData.commissions || false,
        test_account: userData.test_account || false,
        profile_img: userData.profile_img || '',
        cover_image: userData.cover_image || ''
      });

    } catch (error) {
      console.error('Error loading user profile:', error);
      setError(`Failed to load profile: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;

    // Validate file
    const validation = validateFile(file, 'image');
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    try {
      setUploadingImage(true);
      setError('');

      // Upload to Cloudinary
      const result = await uploadToCloudinary(
        file, 
        'image', 
        `users/${type === 'profile' ? 'profiles' : 'covers'}`,
        `${userId}_${type}_${Date.now()}`
      );

      // Update profile state
      setProfile(prev => ({
        ...prev,
        [type === 'profile' ? 'profile_img' : 'cover_image']: result.url
      }));

      setSuccess(`${type === 'profile' ? 'Profile' : 'Cover'} image uploaded successfully!`);

    } catch (error) {
      console.error(`Error uploading ${type} image:`, error);
      setError(`Failed to upload ${type} image: ${error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (type) => {
    if (type === 'profile') {
      setProfile(prev => ({ ...prev, profile_img: '' }));
    } else {
      setProfile(prev => ({ ...prev, cover_image: '' }));
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm the new password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setResettingPassword(true);
    setError('');
    setSuccess('');

    try {
      // Update password using Supabase Admin API
      const { error } = await supabase.auth.admin.updateUserById(userId, {
        password: newPassword
      });

      if (error) {
        throw error;
      }

      setSuccess('Password reset successfully! The user can now log in with the new password.');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordReset(false);

    } catch (error) {
      console.error('Error resetting password:', error);
      setError(`Failed to reset password: ${error.message}`);
    } finally {
      setResettingPassword(false);
    }
  };

  const cancelPasswordReset = () => {
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordReset(false);
    setError('');
  };

  // Handle validation functions
  const validateHandle = (handle) => {
    if (!handle) return { isValid: true, message: '' };
    
    // Check for browser-safe characters (alphanumeric, hyphens, underscores only)
    const handleRegex = /^[a-zA-Z0-9_-]+$/;
    if (!handleRegex.test(handle)) {
      return { 
        isValid: false, 
        message: 'Handle can only contain letters, numbers, hyphens, and underscores. No spaces or special characters allowed.' 
      };
    }
    
    // Check length (3-30 characters)
    if (handle.length < 3) {
      return { isValid: false, message: 'Handle must be at least 3 characters long.' };
    }
    if (handle.length > 30) {
      return { isValid: false, message: 'Handle must be 30 characters or less.' };
    }
    
    // Check for consecutive hyphens or underscores
    if (handle.includes('--') || handle.includes('__') || handle.includes('-_') || handle.includes('_-')) {
      return { isValid: false, message: 'Handle cannot contain consecutive hyphens or underscores.' };
    }
    
    // Check if starts or ends with hyphen/underscore
    if (handle.startsWith('-') || handle.startsWith('_') || handle.endsWith('-') || handle.endsWith('_')) {
      return { isValid: false, message: 'Handle cannot start or end with hyphens or underscores.' };
    }
    
    return { isValid: true, message: '' };
  };

  const checkHandleUniqueness = async (handle, currentUserId) => {
    if (!handle) return { isUnique: true, message: '' };
    
    try {
      const { data: existingUser, error } = await supabase
        .from('users')
        .select('uuid, handle')
        .eq('handle', handle)
        .neq('uuid', currentUserId) // Exclude current user
        .maybeSingle();

      if (error) throw error;
      
      if (existingUser) {
        return { 
          isUnique: false, 
          message: `Handle "${handle}" is already taken by another user.` 
        };
      }
      
      return { isUnique: true, message: '' };
    } catch (error) {
      console.error('Error checking handle uniqueness:', error);
      return { isUnique: false, message: 'Error checking handle availability.' };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profile.first_name || !profile.last_name || !profile.email) {
      setError('Please fill in all required fields (First Name, Last Name, Email)');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profile.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate handle format
    const handleValidation = validateHandle(profile.handle);
    if (!handleValidation.isValid) {
      setError(handleValidation.message);
      return;
    }

    // Check handle uniqueness
    const uniquenessCheck = await checkHandleUniqueness(profile.handle, userId);
    if (!uniquenessCheck.isUnique) {
      setError(uniquenessCheck.message);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Check if email has changed
      const emailChanged = profile.email !== originalProfile.email;
      
      // If email changed, update auth.users first via Edge Function (requires service role)
      if (emailChanged) {
        console.log('Email changed, updating auth.users via Edge Function...');
        
        // Get the current session for authorization
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch('https://api.vibrantcast.com/functions/v1/admin_update_user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            user_id: userId,
            email: profile.email,
          }),
        });

        const result = await response.json();
        
        if (!response.ok || !result.success) {
          // Check for specific errors
          if (result.error?.includes('already registered')) {
            throw new Error('This email is already registered to another account');
          }
          throw new Error(result.error || 'Failed to update auth email');
        }
        console.log('Auth email updated successfully via Edge Function');
      }

      // Update users table
      const { error } = await supabase
        .from('users')
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
          nickname: profile.nickname,
          handle: profile.handle,
          bio: profile.bio,
          mobile_phone: profile.mobile_phone,
          instagram: profile.instagram,
          preferences: {
            home: profile.user_type,
            home_select: false
          },
          commissions: profile.commissions,
          test_account: profile.test_account,
          profile_img: profile.profile_img,
          cover_image: profile.cover_image,
          updated_at: new Date().toISOString()
        })
        .eq('uuid', userId);

      if (error) {
        throw error;
      }

      // Show appropriate success message
      if (emailChanged) {
        setSuccess('Profile updated successfully! Email has been changed in both auth and profile systems. The user can now log in with the new email.');
      } else {
        setSuccess('Profile updated successfully!');
      }
      setOriginalProfile({ ...profile });
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate(-1);
      }, 2000);

    } catch (error) {
      console.error('Error updating profile:', error);
      setError(`Failed to update profile: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="user-profile-edit">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-edit">
      {/* Header */}
      <div className="edit-header">
        <button 
          type="button" 
          className="back-button"
          onClick={handleCancel}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h1>Edit User Profile</h1>
        <div className="header-actions">
          {hasChanges && (
            <span className="unsaved-changes">Unsaved changes</span>
          )}
        </div>
      </div>

      {/* Cover Image Section */}
      <div className="cover-image-section">
        <div className="cover-image-container">
          {profile.cover_image ? (
            <div className="cover-image-wrapper">
              <img 
                src={profile.cover_image} 
                alt="Cover" 
                className="cover-image"
              />
              <div className="cover-image-overlay">
                <label className="image-action-btn">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageUpload('cover', file);
                    }}
                    style={{ display: 'none' }}
                  />
                  <Camera className="w-4 h-4" />
                  {uploadingImage ? 'Uploading...' : 'Change'}
                </label>
                <button
                  type="button"
                  className="image-action-btn delete"
                  onClick={() => removeImage('cover')}
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="cover-image-placeholder">
              <ImageIcon className="w-16 h-16" />
              <p>No cover image</p>
              <label className="upload-btn">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImageUpload('cover', file);
                  }}
                  style={{ display: 'none' }}
                />
                <Upload className="w-4 h-4" />
                {uploadingImage ? 'Uploading...' : 'Upload Cover Image'}
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Main Form */}
      <form onSubmit={handleSubmit} className="profile-edit-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Profile Image Section */}
        <div className="form-section">
          <h3>Profile Image</h3>
          <div className="profile-image-container">
            {profile.profile_img ? (
              <div className="profile-image-wrapper">
                <img 
                  src={profile.profile_img} 
                  alt="Profile" 
                  className="profile-image"
                />
                <div className="profile-image-overlay">
                  <label className="image-action-btn">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleImageUpload('profile', file);
                      }}
                      style={{ display: 'none' }}
                    />
                    <Camera className="w-4 h-4" />
                    {uploadingImage ? 'Uploading...' : 'Change'}
                  </label>
                  <button
                    type="button"
                    className="image-action-btn delete"
                    onClick={() => removeImage('profile')}
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="profile-image-placeholder">
                <label className="image-placeholder-content">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageUpload('profile', file);
                    }}
                    style={{ display: 'none' }}
                  />
                  <User className="w-16 h-16" />
                  <p>No profile image</p>
                  <div className="upload-btn-text">
                    <Upload className="w-4 h-4" />
                    {uploadingImage ? 'Uploading...' : 'Click to upload'}
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">First Name *</label>
              <input
                type="text"
                id="first_name"
                value={profile.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                required
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="last_name">Last Name *</label>
              <input
                type="text"
                id="last_name"
                value={profile.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                required
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                value={profile.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="nickname">Nickname</label>
              <input
                type="text"
                id="nickname"
                value={profile.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="handle">Handle</label>
            <input
              type="text"
              id="handle"
              value={profile.handle}
              onChange={(e) => handleInputChange('handle', e.target.value)}
              placeholder="@username"
              disabled={saving}
              className={profile.handle && !validateHandle(profile.handle).isValid ? 'error' : ''}
            />
            <small>This will be their unique identifier on the platform</small>
            {profile.handle && (
              <div className={`validation-message ${validateHandle(profile.handle).isValid ? 'valid' : 'error'}`}>
                {validateHandle(profile.handle).message || '✓ Handle format is valid'}
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h3>Additional Information</h3>
          
          <div className="form-group">
            <label htmlFor="bio">Bio</label>
            <textarea
              id="bio"
              value={profile.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              rows={4}
              placeholder="Tell us about yourself..."
              disabled={saving}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mobile_phone">Mobile Phone</label>
              <input
                type="tel"
                id="mobile_phone"
                value={profile.mobile_phone}
                onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="instagram">Instagram</label>
              <input
                type="text"
                id="instagram"
                value={profile.instagram}
                onChange={(e) => handleInputChange('instagram', e.target.value)}
                placeholder="@username"
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="user_type">User Type *</label>
              <select
                id="user_type"
                value={profile.user_type}
                onChange={(e) => handleInputChange('user_type', e.target.value)}
                required
                disabled={saving}
              >
                <option value="collector">Collector</option>
                <option value="artist">Artist</option>
              </select>
              <small>Determines the default dashboard and features available to the user.</small>
            </div>
            <div className="form-group">
              {/* Empty group to maintain grid layout */}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={profile.commissions}
                onChange={(e) => handleInputChange('commissions', e.target.checked)}
                disabled={saving}
              />
              Accepts Commissions
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={profile.test_account}
                onChange={(e) => handleInputChange('test_account', e.target.checked)}
                disabled={saving}
              />
              Test Account
            </label>
            <small>Mark this user as a test account (for development/testing purposes)</small>
          </div>
        </div>

        {/* Password Reset Section */}
        <div className="form-section">
          <h3>Password Reset</h3>
          
          {!showPasswordReset ? (
            <div className="password-reset-prompt">
              <p>Reset this user's password to allow them to log in with a new password.</p>
              <button
                type="button"
                className="btn-warning"
                onClick={() => setShowPasswordReset(true)}
                disabled={saving}
              >
                <Key className="w-4 h-4" />
                Reset Password
              </button>
            </div>
          ) : (
            <div className="password-reset-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="new_password">New Password *</label>
                  <input
                    type="password"
                    id="new_password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    disabled={resettingPassword}
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirm_password">Confirm Password *</label>
                  <input
                    type="password"
                    id="confirm_password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    disabled={resettingPassword}
                    minLength={6}
                  />
                </div>
              </div>
              
              <div className="password-reset-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handlePasswordReset}
                  disabled={resettingPassword || !newPassword || !confirmPassword}
                >
                  {resettingPassword ? (
                    <>
                      <div className="spinner"></div>
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4" />
                      Reset Password
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={cancelPasswordReset}
                  disabled={resettingPassword}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <>
                <div className="spinner"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfileEdit;
